import type { GetStaticPaths, GetStaticProps } from 'next';
import React, { useEffect } from 'react';

import { ssgClient } from '../../../apollo/api/avantArteClient';
import { storyblokApolloClient } from '../../../apollo/storyblok/client';
import Layout from '../../../components/Layout';
import Artists from '../../../components/PageTypes/Artists';
import {
  ARTIST_INDEX_PAGE_TYPE,
  ONE_HOUR_IN_SECONDS,
} from '../../../constants';
import { AvantArteArtistsSearchGateway } from '../../../gateways/AvantArteArtistsSearchGateway';
import { StoryblokArtistsGateway } from '../../../gateways/StoryblokArtistsGateway';
import { pathFor, toCanonicalUrl } from '../../../helpers/links';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import type { AugmentedArtistCard } from '../../../types/Artists';
import { TrackPageView } from '../../../utils/analytics';
import { augmentArtists } from '../../../utils/artists';
import { getStoryblokPage, useStoryblok } from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';

type ArtistsPageData = {
  artists: AugmentedArtistCard[];
  pageNumber: number;
};

export default function Page({
  locale,
  locales,
  ogImageUrl,
  pageData,
  preview,
  story,
}: PageProps) {
  useEffect(() => {
    TrackPageView({ page: ARTIST_INDEX_PAGE_TYPE });
  }, []);

  const enableBridge = preview;
  story = useStoryblok(story, enableBridge, locale);

  const data = pageData as ArtistsPageData;
  story.content = {
    ...story.content,
    artists: data.artists,
    pageNumber: data.pageNumber,
  };

  return (
    <Layout
      canonicalUrl={toCanonicalUrl({
        path: pathFor({ page: 'ARTISTS_INDEX', pageNumber: data.pageNumber }),
        locale,
        hostname: process.env.NEXT_PUBLIC_HOSTNAME!,
      })}
      description={story.content.seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={story.content.seo?.title}
    >
      <Artists blok={story.content} />
    </Layout>
  );
}

export const getStaticProps: GetStaticProps<any, { number: string }> = async (
  ctx,
) => {
  const { locale, locales, params, preview = false } = ctx;
  const pageNumber = params?.number ? parseInt(params?.number, 10) : 1;
  const page = await getStoryblokPage('cdn/stories/artists', {
    locale,
    preview,
  });

  if (page === null) {
    throw new Error('Storyblok content not found');
  }

  const artistsGateway = new StoryblokArtistsGateway(storyblokApolloClient);
  const artists = await artistsGateway.allArtists();
  const artistsSlugs = artists.map(
    (artist) => artist.link.split('/').pop() || '',
  );

  const internalArtists = await new AvantArteArtistsSearchGateway(
    ssgClient(ctx),
  ).getArtistsBySlug(artistsSlugs);

  const augmentedArtists = await augmentArtists(artists, internalArtists);

  const pageData: ArtistsPageData = {
    artists: augmentedArtists,
    pageNumber,
  };

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: { key: 'og:artists.index.title' },
    imageSrc:
      'https://a.storyblok.com/f/119968/4000x2667/846735f337/parra-atelier-2.jpg',
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
    locale,
  });

  return {
    props: {
      story: page.data ? page.data.story : false,
      pageData,
      preview,
      locale,
      locales,
      ogImageUrl,
      ...(await pageTranslations(locale!, ['filters', 'artists', 'og'])),
    },
    revalidate: ONE_HOUR_IN_SECONDS,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [{ params: { number: '1', locale: 'en' } }],
    fallback: 'blocking',
  };
};
